/**
 * CountRenderer - Renderer for loop iteration count settings
 *
 * Allows setting the maximum number of iterations for loops (ForEach, Until).
 */

import { makeStyles, tokens, Text, SpinButton, Field } from '@coreai/fluentui-react';
import type { SettingRendererProps } from './registry';
import { registerSettingRenderer } from './registry';

import {
  settingDescriptionStyle,
  settingHeaderStyle,
  settingLabelStyle,
} from './settingRendererStyles';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
  },
  header: settingHeaderStyle,
  label: settingLabelStyle,
  description: settingDescriptionStyle,
  inputContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalXS,
  },
  spinButton: {
    width: '150px',
  },
  hint: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground4,
    fontStyle: 'italic',
  },
});

export interface CountRendererProps extends SettingRendererProps<string | number> {
  label?: string;
  description?: string;
}

/**
 * CountRenderer component for loop count configuration
 */
export const CountRenderer = ({
  setting,
  onChange,
  readOnly = false,
  label = 'Loop Count',
  description = 'Maximum number of loop iterations',
}: CountRendererProps) => {
  const styles = useStyles();

  if (!setting.isSupported) {
    return null;
  }

  const currentValue = typeof setting.value === 'number'
    ? setting.value
    : typeof setting.value === 'string'
      ? parseInt(setting.value, 10) || 5000
      : 5000;

  const handleChange = (_: React.SyntheticEvent, data: { value?: number | null }) => {
    if (data.value !== undefined && data.value !== null) {
      onChange(data.value);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Text className={styles.label}>{label}</Text>
      </div>
      <Text className={styles.description}>{description}</Text>
      <div className={styles.inputContainer}>
        <Field>
          <SpinButton
            className={styles.spinButton}
            value={currentValue}
            onChange={handleChange}
            min={1}
            max={100000}
            step={100}
            disabled={readOnly}
            aria-label={label}
          />
        </Field>
        <Text className={styles.hint}>Default: 5000</Text>
      </div>
    </div>
  );
};

CountRenderer.displayName = 'CountRenderer';

registerSettingRenderer('count', CountRenderer as React.ComponentType<SettingRendererProps<unknown>>);
